import type { ComponentPropsWithoutRef, CSSProperties } from 'react';
import { cn } from '../../utilities/classname';

/**
 * Fades and slides a block into view. Pure CSS — no JavaScript.
 *
 * In modern browsers (Chrome 115+, Safari 17.4+, Firefox 137+) the animation
 * is tied to scroll position via `animation-timeline: view()`, so the block
 * fades in as it enters the viewport. Older browsers in the project's
 * browserslist fall back to the time-based behaviour: the animation runs
 * once on page load, optionally staggered.
 *
 * `stagger` cascades siblings (each step delays the time-based fallback
 * by 80ms; in scroll-driven browsers the per-element scroll position
 * naturally cascades them as you scroll past). Respects
 * `prefers-reduced-motion` via CSS.
 *
 * `durationMs` and `cover` tune the two knobs `.reveal` exposes as CSS
 * variables, for a large element that reads as arriving too fast at the
 * defaults (600ms, completing 25% into the cover phase). Both are typed here
 * rather than left to the caller's `style`, because writing a CSS custom
 * property into a `CSSProperties` needs an assertion — one that belongs in this
 * component, once, next to the `--reveal-stagger` one it already carries.
 *
 * `inline` swaps the timeline to the nearest scrollport's INLINE axis, for a
 * child of a horizontally scrolling container: without it every item in such a
 * strip reveals together on vertical scroll, rather than arriving as you scroll
 * along the strip. See `.reveal-inline` in `base.css`.
 *
 * Any extra HTML attributes (data-testid, aria-hidden, role, etc.) pass
 * through to the wrapper div.
 */
export const Reveal: React.FC<
  Omit<ComponentPropsWithoutRef<'div'>, 'ref'> & {
    stagger?: number;
    durationMs?: number;
    /** How far into the cover phase the animation completes, in percent. */
    cover?: number;
    inline?: boolean;
  }
> = ({ children, className, stagger = 0, durationMs, cover, inline, style, ...rest }) => {
  const vars: Record<string, string | number> = {};

  if (stagger) { vars['--reveal-stagger'] = stagger; }
  if (durationMs !== undefined) { vars['--reveal-duration'] = `${durationMs}ms`; }
  if (cover !== undefined) { vars['--reveal-cover'] = `${cover}%`; }

  const mergedStyle: CSSProperties | undefined = Object.keys(vars).length > 0
    ? { ...style, ...vars } as CSSProperties
    : style;

  return (
    <div className={cn(inline ? 'reveal-inline' : 'reveal', className)} style={mergedStyle} {...rest}>
      {children}
    </div>
  );
};
